import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { Clock } from '~/Components/Icons';
import { EventType } from '~/Models/DTO/Event.model';
import { Schedule } from '~/Components/Icons/Schedule';
import { ExtendedEvent } from '~/Screens/Events/EventCreate/ExtendedEvent.model';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { useProvider } from '~/Hooks';

export interface IUseScheduleSectionInputsProps {
  eventType: EventType;
  event?: ExtendedEvent;
  bookingSummary?: IBookingSummaryEvent;
}

export const useScheduleSectionInputs = ({
  eventType,
  event,
  bookingSummary,
}: IUseScheduleSectionInputsProps): IItemCreationSubsectionInputProps<ExtendedEvent>[] => {
  const { t } = useTranslation();
  const { isUsingThePartnerTool } = useProvider();

  const input: { [key: string]: IItemCreationSubsectionInputProps<ExtendedEvent> } = useMemo(
    () => ({
      startDate: {
        icon: <Schedule />,
        label: t('time.startDate'),
        placeholder: t('time.startDate'),
        type: ItemCreationValidInput.DATE,
        disabled: isUsingThePartnerTool && !!bookingSummary?.bookingsBooked,
        name: 'startDate',
      },
      startTime: {
        icon: <Clock />,
        label: t('time.startTime'),
        placeholder: t('time.startTime'),
        type: ItemCreationValidInput.TIME,
        name: 'startTime',
        disabled: isUsingThePartnerTool && !!bookingSummary?.bookingsBooked,
        hasAnError: !!event?.startTime && !!event?.endTime && event?.startTime > event?.endTime,
      },
      endTime: {
        icon: <Clock />,
        label: t('time.endTime'),
        placeholder: t('time.endTime'),
        type: ItemCreationValidInput.TIME,
        name: 'endTime',
        disabled: isUsingThePartnerTool && !!bookingSummary?.bookingsBooked,
        hasAnError: !!event?.startTime && !!event?.endTime && event?.startTime > event?.endTime,
      },
    }),
    [eventType, event, bookingSummary?.bookingsBooked, isUsingThePartnerTool],
  );

  if (eventType === EventType.FREE_TRAINING) {
    return [input.startDate];
  }

  return [input.startDate, input.startTime, input.endTime];
};
